import memoize from 'memoize-one';

import { FetchError, type SegmentBatchDef } from './types';

export type Options = {
	abortController?: AbortController;
	batch: SegmentBatchDef;
	timeout?: number;
	url: string;
};

export const sendEvents = async (options: Options): Promise<Response> => {
	const signal = createOrGetAbortController(options)?.signal;

	const corsOptions = getCorsForUrl(options.url);

	const response = await fetch(options.url, {
		method: 'POST',
		// Content-type has to be text/plain in order to prevent unnecessary `OPTIONS` requests.
		headers: { 'Content-Type': 'text/plain' },
		body: JSON.stringify(options.batch),
		signal,
		...corsOptions,
	});

	if (response.status !== 200) {
		throw new FetchError(
			`HTTP Error ${response.status} (${response.statusText})`,
			response.status,
			response.statusText,
		);
	}

	try {
		let responseBody = await response.clone().json();
		if ((responseBody as { success?: boolean }).success === true) {
			return response;
		}
	} catch {
	}

	throw new FetchError('Invalid HTTP Response (Invalid response body)', 200, 'Invalid response body');
};

const createOrGetAbortController = (options: Options): AbortController | undefined => {
	if (options.abortController) {
		return options.abortController;
	} else if (options.timeout) {
		const abortController = new AbortController();

		// After a fetch has completed, calling abort is ignored.
		setTimeout(() => {
			abortController.abort();
		}, options.timeout);

		return abortController;
	}

	return undefined;
};

// To ensure we always have cookies, we should check if the origins match.
// If they dont, we need to set mode to `cors` and credentials to `include`.
const getCorsForUrl = memoize((url: string): RequestInit => {
	const origin = new URL(url).origin;
	if (origin === location.origin) {
		return {};
	} else {
		return {
			mode: 'cors',
			credentials: 'include',
		};
	}
});
